'use client';

import { useNavigationLoading } from '@lib/hooks/use-navigation-loading';
import { cn } from '@lib/util/cn';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { Spinner } from '@modules/common/icons';

const CTA_CORE =
  'relative inline-flex items-center justify-center px-6 font-bold text-sm rounded-full transition-colors duration-200 whitespace-nowrap';

const CTA_SIZES = {
  sm: 'py-2.5',
  md: 'py-3',
} as const;

const CTA_VARIANT_CLASSES = {
  primary:
    'bg-surface-interactive-default text-fg-neutral-strong hover:bg-surface-interactive-soft',
  secondary: 'border-2 border-white text-white hover:bg-white/10',
  outlined:
    'border-2 border-fg-neutral-strong text-fg-neutral-strong hover:bg-black/5',
} as const;

export type CtaVariant = keyof typeof CTA_VARIANT_CLASSES;

function ctaClassName(
  variant: CtaVariant,
  size: keyof typeof CTA_SIZES = 'sm',
): string {
  return cn(CTA_CORE, CTA_SIZES[size], CTA_VARIANT_CLASSES[variant]);
}

type SectionCtaProps = {
  label: string;
  href: string;
  variant: CtaVariant;
  ariaLabel?: string;
  /**
   * Extra context appended as visually-hidden text (not `display:none`/`visibility:hidden`,
   * so it's still picked up by `innerText`). Use for short labels like "Learn more" that
   * would otherwise fail Lighthouse's SEO "descriptive link text" audit, without changing
   * the visible label. Prefer this over `ariaLabel` here — `aria-label` replaces the
   * accessible name entirely and is ignored by that audit (it reads DOM text, not ARIA).
   */
  visuallyHiddenSuffix?: string;
  size?: keyof typeof CTA_SIZES;
};

const SectionCta = ({
  label,
  href,
  variant,
  ariaLabel,
  visuallyHiddenSuffix,
  size = 'sm',
}: SectionCtaProps) => {
  const { navigate, isPending } = useNavigationLoading();

  return (
    <LocalizedClientLink
      href={href}
      className={cn(
        ctaClassName(variant, size),
        isPending && 'pointer-events-none',
      )}
      aria-label={ariaLabel}
      aria-busy={isPending ? 'true' : undefined}
      onClick={(e) => {
        e.preventDefault();
        if (!isPending) {
          navigate(href);
        }
      }}
    >
      <span className={isPending ? 'invisible' : undefined}>
        {label}
        {visuallyHiddenSuffix && (
          <span className="sr-only"> {visuallyHiddenSuffix}</span>
        )}
      </span>
      {isPending && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size="16" aria-hidden="true" />
        </span>
      )}
    </LocalizedClientLink>
  );
};

export default SectionCta;
